import { useAuth0 } from '@auth0/auth0-vue'
import { useQueryClient } from '@tanstack/vue-query'
import { useIntervalFn, useLocalStorage, until } from '@vueuse/core'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import type { Permission, PermissionAction, Role, Scope, Standard, User } from '../interfaces'
import { useUserTokenQuery } from '../queries/user.query'
import { setAxiosHeaders, setAuthAxiosHeaders } from '../utils/axios.util'

export const useAuthStore = defineStore('auth', () => {
  const router = useRouter()
  const queryClient = useQueryClient()
  const { user: auth0User, getAccessTokenSilently, loginWithRedirect, isAuthenticated, logout: aut0Logout, isLoading } = useAuth0()

  const user = ref<User>()
  const isFirstLogin = ref(false)
  const accessToken = ref('')
  const standard = useLocalStorage<Standard | ''>('selectedStandard', '')
  const standards = computed<Standard[]>(() => {
    if (user.value) {
      return user.value[`${import.meta.env.VITE_AUTH0_NAMESPACE}/standards`] ?? []
    } else {
      return []
    }
  })

  const workspaceRole = computed<Standard>(() => {
    if (user.value) {
      return user.value[`${import.meta.env.VITE_AUTH0_NAMESPACE}/roles`].find((role) => role.startsWith(standard.value)) ?? ''
    } else {
      return ''
    }
  })

  const setEntryRedirect = (path: string) => {
    localStorage.setItem('redirect', path)
  }

  const fetchAccessToken = () => {
    return getAccessTokenSilently()
      .then((token) => {
        if (token === accessToken.value) {
          return
        }

        setAxiosHeaders({ standard, token })
        setAuthAxiosHeaders({ standard, token })
        accessToken.value = token
      })
      .catch((e) => {
        setAxiosHeaders({ standard, token: '' })
        setAuthAxiosHeaders({ standard, token: '' })

        console.error(e)
      })
  }

  const { data: userToken, isLoading: isloadingUser } = useUserTokenQuery({ enabled: computed(() => Boolean(accessToken.value)) })

  const checkAuth = async () => {
    await until(isLoading).toBe(false)
    await fetchAccessToken()

    if (isAuthenticated.value) {
      const redirect = localStorage.getItem('redirect')

      await router.push(redirect || '/')
      localStorage.removeItem('redirect')
    } else {
      await loginWithRedirect()
    }
  }

  const permissions = computed<Permission[]>(() => userToken?.value?.permissions ?? [])

  const setPermissions = async () => {
    if (!isAuthenticated.value) {
      accessToken.value = ''
      user.value = undefined

      localStorage.setItem('redirect', window.location.pathname)
      await loginWithRedirect()
    }

    user.value = {
      ...(auth0User.value as User),
      edit: false
    }

    if (standards.value.length === 1) {
      standard.value = standards.value[0]
    }

    isFirstLogin.value = user.value && user.value.name === user.value.email

    setAxiosHeaders({ standard, token: accessToken })
    setAuthAxiosHeaders({ standard, token: accessToken })
  }

  const logout = async () => {
    await aut0Logout()
    await fetchAccessToken()

    setEntryRedirect(router.currentRoute.value.fullPath)
  }

  watch([isloadingUser, userToken], () => setPermissions())

  watch(standard, async () => {
    setAxiosHeaders({ standard, token: accessToken })
    setAuthAxiosHeaders({ standard, token: accessToken })
    await queryClient.invalidateQueries()
  })

  // check session every 5 minutes
  useIntervalFn(async () => {
    await fetchAccessToken()
  }, 1000 * 60 * 5)

  const toggleUser = () => {
    user.value = {
      ...user.value,
      edit: !user.value?.edit
    }
  }

  function hasPermission(action: PermissionAction, target: Scope) {
    return permissions?.value.some((permission) => permission === `${standard.value}/${action}:${target}` || permission === `${action}:${target}`)
  }

  function hasRole(role: Role) {
    return userToken.value?.roles.includes(`${standard.value}/${role}`)
  }

  function isAdmin() {
    return (['METRAS_SUPER_ADMIN', 'METRAS_ADMIN', 'WORKSPACE_METRAS_ADMIN', 'WORKSPACE_SUPER_ADMIN', 'WORKSPACE_ADMIN'] as Role[]).some((role) => hasRole(role))
  }

  return {
    user,
    standard,
    standards,
    workspaceRole,
    toggleUser,
    accessToken,
    isFirstLogin,
    setEntryRedirect,
    fetchAccessToken,
    checkAuth,
    setPermissions,
    loginWithRedirect,
    isAuthenticated,
    hasPermission,
    hasRole,
    isAdmin,
    logout,
    isLoading
  }
})

// make sure to pass the right store definition, `useDirectoryStore` in this case.
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot))
}
